import $ from 'jquery';
import {getSetting, toCents, convertFormToFields} from "@wc-stripe/utils";
import actions from '@wc-stripe/actions';

/**
 * Represents the current product on a single product page.
 *
 * ## Price vs Total
 * `data.price` is always the unit base price and is never modified by addon integrations.
 * `data.total` is the per-unit total override, set by addon integrations to reflect
 * additional costs (e.g. product add-ons). getTotal() multiplies this by quantity.
 *
 * When a variation is selected, `total` is reset to the variation's display_price so that
 * getTotal() returns the correct value immediately. Addon plugins are expected to re-fire
 * their recalculation events on variation change (e.g. WooCommerce Product Addons fires
 * `updated_addons`), which will update `total` with the new base price + addon costs.
 *
 * ## product_updated event
 * All internal state changes go through update(), which fires the `product_updated` action.
 * Listeners (Cart.js, ProductController.js) subscribe to this single event rather than
 * individual events like `found_variation` or `quantity_change`. Those legacy events are
 * still fired for backward compatibility with 3rd party code.
 *
 * ## 3rd party integration API
 * External code (addon plugins) can signal price changes via:
 *   product.update({ total: newUnitTotal })
 * This triggers `product_updated` so payment elements recalculate automatically.
 */
class Product {

    variation = null;

    _excludedKeys = ['add-to-cart', 'quantity', 'variation_id'];

    constructor(settings = {}) {
        this.actions = actions; // Shared actions instance (singleton)
        this.data = this.default_data = settings || {};
        // Pre-initialize variation state so isVariationSelected() works before found_variation fires.
        if (this.data.variation_id && this.data.attributes) {
            this.variation = {variation_id: this.data.variation_id, attributes: this.data.attributes};
        }
        $(document.body).on('change', '[name="quantity"]', this.onQuantityChange.bind(this));
        $('form.cart')
            .on('found_variation', this.foundVariation.bind(this))
            .on('reset_data', this.resetVariationData.bind(this));
        this.actions.addFilter('cart_calculation_data', this.addCartCalculationData.bind(this));
        this.actions.addAction('after_cart_calculation', this.onAfterCartCalculation.bind(this));
        // iAPI single-product block bridge
        this.actions.addAction('single_product_variation_changed', this.onBlockVariationChanged.bind(this));
        this.actions.addAction('single_product_variation_reset', this.resetVariationData.bind(this));
    }

    needsShipping() {
        return this.actions.applyFilters('product_needs_shipping', this.data?.needsShipping);
    }

    /**
     * Determines whether a server-side cart calculation should be triggered.
     *
     * Hard exits (always false):
     *   - Product is out of stock
     *   - Variable product with no variation selected yet
     *
     * Physical products: only recalculate when qty or variation actually changed,
     * since shipping costs won't change otherwise.
     *
     * Virtual products: always recalculate (no shipping, but price may differ per qty/variation).
     */
    needsCartCalculation() {
        let bool = true;
        if (!this.isInStock()) {
            bool = false;
        } else if (this.isVariableProduct() && !this.isVariationSelected()) {
            bool = false;
        } else if (this._prevData) {
            bool = this.data.qty !== this._prevData.qty
                || this.data.variation_id !== this._prevData.variation_id
                || this.data.attributesHash !== this._prevData.attributesHash;
        }
        return this.actions.applyFilters('product_needs_cart_calculation', bool, this);
    }

    getLineItems() {
        return this.data.lineItems || [];
    }

    getLineItem() {
        const id = this.isVariationSelected() ? parseInt(this.getVariationId()) : this.getId();
        return this.getLineItems().find(item => item.id === id) ?? null;
    }

    /**
     * Merge partial data into this.data and notify all listeners via `product_updated`.
     * This is the single entry point for all state changes, including those from
     * 3rd party addon integrations.
     *
     * @param {Object} data - Partial data to merge into this.data
     * @param {Boolean} silent
     */
    update(data = {}, silent = false) {
        const changed = Object.keys(data).some(key => data[key] !== this.data[key]);
        if (!changed) return;
        this._prevData = {...this.data};
        this.data = {...this.data, ...data};
        if (!silent) {
            this.actions.doAction('product_updated', this, this._prevData);
        }
    }

    onQuantityChange(e) {
        const qty = this.getQuantity();
        this.update({qty, total: this.getPrice() * qty});
    }

    foundVariation(e, variation) {
        this.variation = {...variation};
        const varData = this.getVariationData();
        const attributesHash = Object.keys(varData).sort().map(k => `${k}:${varData[k]}`).join('|');
        // Set total to price × qty so getTotal() returns the correct value immediately.
        // Addon plugins that re-fire on variation change (e.g. updated_addons) will
        // subsequently override total with the full computed value including their costs.
        this.update({
            price: variation.display_price,
            priceCents: toCents(variation.display_price),
            total: variation.display_price * this.getQuantity(),
            isInStock: variation.is_in_stock,
            variation_id: variation.variation_id,
            needsShipping: !variation.is_virtual,
            attributesHash
        });
        // Still fired for any 3rd party code that listens to found_variation directly
        this.actions.doAction('found_variation', this.needsCartCalculation(), this, this._prevData);
    }

    /**
     * Handles variation selection from the woocommerce/add-to-cart-with-options block.
     * The iAPI store exposes variation data in Store API format (prices in minor units,
     * no is_virtual field), so we translate what's available and omit needsShipping
     * to preserve whatever the PHP-rendered product data set on page load.
     */
    onBlockVariationChanged(variationId, variation) {
        if (!variation) return;
        const {price, currency_minor_unit} = variation.prices ?? {};
        const displayPrice = price
            ? parseInt(price, 10) / Math.pow(10, currency_minor_unit ?? 2)
            : 0;

        this.variation = {variation_id: variationId, attributes: variation.attributes};
        this.update({
            price: displayPrice,
            priceCents: toCents(displayPrice),
            total: displayPrice * this.getQuantity(),
            isInStock: variation.is_in_stock ?? variation.is_purchasable ?? true,
            variation_id: variationId,
        });
        this.actions.doAction('found_variation', this.needsCartCalculation(), this, this._prevData);
    }

    resetVariationData() {
        this.variation = null;

        /**
         * Some 3rd party plugins trigger the variation reset event even if the product isn't a variation.
         * For example, the woocommerce-product-bundles plugin.
         */
        if (!this.isVariableProduct()) {
            return;
        }
        // Restore all data to the original product defaults (no variation selected).
        this.update({
            variation_id: null,
            price: this.default_data.price,
            priceCents: toCents(this.default_data.price),
            total: this.default_data.total,
            isInStock: this.default_data.isInStock,
            needsShipping: this.default_data.needsShipping,
            attributesHash: '',
        });
        // Still fired for any 3rd party code that listens to reset_variation directly
        this.actions.doAction('reset_variation', this);
    }

    onAfterCartCalculation(cart) {
        const productLineItems = cart.getLineItems().filter(item => item.type === 'product');
        if (productLineItems.length > 0) {
            this.update({lineItems: productLineItems});
        }
    }

    addCartCalculationData(data) {
        return {
            ...data,
            product_id: this.getId(),
            variation_id: this.isVariableProduct() ? this.getVariationId() : 0,
            qty: this.getQuantity(),
            ...this.getVariationData(),
            ...convertFormToFields($('form.cart'), this._excludedKeys)
        }
    }

    /**
     * Checks if the product is in stock. If a variation is selected then this method
     * returns stock for the variation.
     * @returns {boolean}
     */
    isInStock() {
        return !!this.data.isInStock;
    }

    getQuantity() {
        return parseInt($('[name="quantity"]').val()) || 0;
    }

    /**
     * Returns the unit base price. Never modified by addon integrations.
     */
    getPrice() {
        return this.actions.applyFilters('product_price', parseFloat(this.data?.price || 0), this);
    }

    getPriceCents() {
        return this.actions.applyFilters('product_price_cents', this.data?.priceCents ?? toCents(this.getPrice()), this);
    }

    /**
     * Returns the full total for the current quantity.
     *
     * When data.total is explicitly set by an addon integration it is used as-is,
     * since addon costs may already be quantity-adjusted (e.g. quantity-based addons
     * in WooCommerce Product Addons). Otherwise falls back to price × qty.
     *
     *   getTotal() = data.total ?? (price * qty)
     */
    getTotal() {
        const total = this.data.total ?? parseFloat(this.getPrice() * this.getQuantity());
        return this.actions.applyFilters('product_total', parseFloat(total), this);
    }

    getTotalCents() {
        return this.actions.applyFilters('product_total_cents', toCents(this.getTotal()), this);
    }

    isVariableProduct() {
        return this.data.type.indexOf('variable') > -1;
    }

    isVariationSelected() {
        if (this.variation?.variation_id) {
            return true;
        }
        const val = $('input[name="variation_id"]').val();
        return !!val && "0" !== val;
    }

    getVariationData() {
        if (this.isVariableProduct()) {
            const attributes = this.variation ? {...this.variation.attributes} : {};
            const elements = document.querySelectorAll('.variations [name^="attribute_"]');
            if (elements) {
                elements.forEach((element) => {
                    if (!(element.name in attributes) || (attributes[element.name] === '')) {
                        attributes[element.name] = element.value || '';
                    }
                });
            }
            return attributes;
        }
        return {};
    }

    getId() {
        return parseInt(this.data?.id);
    }

    getVariationId() {
        if (this.variation) {
            return this.variation.variation_id;
        }
        return $('[name="variation_id"]').val();
    }
}

export default new Product(getSetting('product'));
